/* ──────────────────────────────────────────────────────────────────────────
 * Become a Partner — Global Reseller Program intake flow.
 *
 * 1:1 React port of `reseller_become_partner_flow.html`. Opened from the
 * Dashboard-New → Global Reseller Program → "Become a partner" CTA. Three
 * steps: choose partner type → tailored details form → submit. On submit it
 * follows the exact same path as every other Contact-Us form on the platform:
 * POST /api/contact/lead (lead_type "reseller_partner") so the application lands
 * in Admin Center → Contact Leads. Styles scoped under `.dash-new-root .bp*`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = {
  ecom: {
    icon: "ti-building-store", iconBg: "#FAEEDA", iconColor: "#854F0B", tag: "Enterprise",
    name: "E-commerce platform", desc: "Marketplaces with large resellers (₹5 Cr+ profit)",
    label: "E-commerce platform", org: "Platform / company name",
    fields: [
      { k: "website", l: "Website / marketplace URL", p: "https://" },
      { k: "resellers", l: "Approx. number of resellers / sellers", p: "e.g. 1,200" },
      { k: "sellers_5cr", l: "Number of sellers with ₹5 Cr+ profit", p: "e.g. 40" },
    ],
  },
  cafirm: {
    icon: "ti-calculator", iconBg: "#E1F5EE", iconColor: "#085041", tag: "Channel",
    name: "CA & CS firm", desc: "Practices with SME client books",
    label: "CA & CS firm", org: "Firm name",
    fields: [
      { k: "firm_reg", l: "ICAI / ICSI firm registration no.", p: "e.g. 012345C" },
      { k: "sme_clients", l: "Number of SME / MSME clients", p: "e.g. 60" },
      { k: "region", l: "Primary city / region served", p: "e.g. Pune" },
    ],
  },
  cfo: {
    icon: "ti-user-dollar", iconBg: "#E6F1FB", iconColor: "#0C447C", tag: "Individual",
    name: "Independent / fractional CFO", desc: "Part-time finance leaders across MSME boards",
    label: "Independent / fractional CFO", org: "Your practice / brand name",
    fields: [
      { k: "experience", l: "Years of finance leadership experience", p: "e.g. 12" },
      { k: "boards", l: "Number of MSME boards / clients you advise", p: "e.g. 5" },
      { k: "linkedin", l: "LinkedIn profile (optional)", p: "https://linkedin.com/in/" },
    ],
  },
  bank: {
    icon: "ti-building-bank", iconBg: "#EEEDFE", iconColor: "#3C3489", tag: "Institutional",
    name: "Bank / NBFC", desc: "Relationship-led lending institutions",
    label: "Bank / NBFC", org: "Institution name",
    fields: [
      { k: "rbi", l: "RBI registration / license type", p: "e.g. NBFC-ICC" },
      { k: "book_size", l: "MSME lending book size (indicative)", p: "e.g. ₹250 Cr" },
      { k: "branches", l: "Branches / regions covered", p: "e.g. Pan-India, 80 branches" },
    ],
  },
};
const TYPE_ORDER = ["ecom", "cafirm", "cfo", "bank"];

const BecomePartnerFlow = ({ onBack }) => {
  const [step, setStep] = useState(1); // 1 choose | 2 form | 3 done
  const [typeKey, setTypeKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", desig: "", email: "", mobile: "", org: "", note: "", dyn: {} });

  const t = typeKey ? TYPES[typeKey] : null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDyn = (k, v) => setForm((f) => ({ ...f, dyn: { ...f.dyn, [k]: v } }));

  const choose = (key) => {
    setTypeKey(key);
    setForm((f) => ({ ...f, dyn: {} }));
    setTimeout(() => setStep(2), 150);
  };

  const validate = () => {
    if (!form.name.trim()) return "Contact name is required";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) return "A valid work email is required";
    if (!form.mobile.trim() || form.mobile.trim().length < 7) return "A valid mobile number is required";
    if (!form.org.trim()) return `${t.org} is required`;
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const lines = [
        `Partner type: ${t.label}`,
        form.desig.trim() ? `Designation: ${form.desig.trim()}` : null,
        `${t.org}: ${form.org.trim()}`,
        ...t.fields.map((f) => (form.dyn[f.k]?.trim() ? `${f.l}: ${form.dyn[f.k].trim()}` : null)),
        form.note.trim() ? `Notes: ${form.note.trim()}` : null,
      ].filter(Boolean);

      await axios.post(`${API_URL}/api/contact/lead`, {
        full_name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        module: `Global Reseller Program — ${t.label}`,
        query: lines.join("\n"),
        lead_type: "reseller_partner",
      });
      toast.success("Application submitted! Our partnerships team will reach out within 2 business days.");
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bp" data-testid="become-partner-flow">
      <div className="bp-head">
        <div className="bp-kick"><i className="ti ti-world-share" aria-hidden="true" /> Global Reseller Program · Become a partner</div>
        <div className="bp-h">Join the Pan-India referral network</div>
        <div className="bp-sub">Choose your partner type, tell us about your business, and our partnerships team will reach out to set up your 25% revenue-share account and referral dashboard.</div>
        <div className="steps">
          <div className={`stp ${step === 1 ? "on" : ""}`}><span className="stp-n">1</span> Choose partner type</div>
          <div className="stp-line" />
          <div className={`stp ${step === 2 ? "on" : ""}`}><span className="stp-n">2</span> Your details</div>
          <div className="stp-line" />
          <div className={`stp ${step === 3 ? "on" : ""}`}><span className="stp-n">3</span> Submit</div>
        </div>
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="bp-card" data-testid="bp-step-type">
          <div className="sec-t">Which best describes you?</div>
          <div className="type-grid">
            {TYPE_ORDER.map((key) => {
              const tp = TYPES[key];
              return (
                <button key={key} className={`tp ${typeKey === key ? "on" : ""}`} onClick={() => choose(key)} data-testid={`bp-type-${key}`}>
                  <div className="tp-ic" style={{ background: tp.iconBg, color: tp.iconColor }}><i className={`ti ${tp.icon}`} aria-hidden="true" /></div>
                  <div>
                    <div className="tp-n">{tp.name}</div>
                    <div className="tp-d">{tp.desc}</div>
                    <span className="tp-tag" style={{ background: tp.iconBg, color: tp.iconColor }}>{tp.tag}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="bp-foot" style={{ marginTop: 14 }}>
            <span className="note"><i className="ti ti-info-circle" style={{ fontSize: 13, color: "#534AB7" }} aria-hidden="true" /> Each partner type has a slightly different intake — pick one to continue.</span>
            <button className="back" onClick={onBack} data-testid="bp-exit">← Back to program</button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && t && (
        <div className="bp-card" data-testid="bp-step-form">
          <div className="dyn-banner">
            <i className="ti ti-circle-check" style={{ fontSize: 15, color: "#534AB7" }} aria-hidden="true" />
            <span className="dyn-t">Applying as: <strong>{t.label}</strong></span>
          </div>

          <div className="sec-t">{t.label} — your details</div>

          <div className="row2">
            <div className="fg"><label className="fl">Contact name <span className="req">*</span></label><input className="fi" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="bp-name" /></div>
            <div className="fg"><label className="fl">Designation</label><input className="fi" value={form.desig} onChange={(e) => set("desig", e.target.value)} placeholder="e.g. Partner / Director" data-testid="bp-desig" /></div>
          </div>
          <div className="row2">
            <div className="fg"><label className="fl">Work email <span className="req">*</span></label><input className="fi" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" data-testid="bp-email" /></div>
            <div className="fg"><label className="fl">Mobile <span className="req">*</span></label><input className="fi" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/[^\d+\-\s()]/g, ""))} placeholder="+91" data-testid="bp-mobile" /></div>
          </div>

          <div className="fg"><label className="fl">{t.org} <span className="req">*</span></label><input className="fi" value={form.org} onChange={(e) => set("org", e.target.value)} data-testid="bp-org" /></div>

          {t.fields.map((f) => (
            <div className="fg" key={f.k}>
              <label className="fl">{f.l}</label>
              <input className="fi" value={form.dyn[f.k] || ""} onChange={(e) => setDyn(f.k, e.target.value)} placeholder={f.p} data-testid={`bp-dyn-${f.k}`} />
            </div>
          ))}

          <div className="fg"><label className="fl">Anything else we should know?</label><textarea className="ft" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Tell us about your network, expected referral volume, regions you cover…" data-testid="bp-note" /></div>

          <div className="bp-foot">
            <button className="back" onClick={() => setStep(1)} disabled={submitting} data-testid="bp-back">← Back</button>
            <span className="note"><i className="ti ti-shield-check" style={{ fontSize: 13, color: "#0F6E56" }} aria-hidden="true" /> No commitment — our partnerships team reviews and reaches out within 2 business days.</span>
            <button className="submit" onClick={handleSubmit} disabled={submitting} data-testid="bp-submit">{submitting ? "Submitting…" : "Submit application →"}</button>
          </div>
        </div>
      )}

      {/* STEP 3 — confirmation */}
      {step === 3 && t && (
        <div className="bp-card" data-testid="bp-step-done">
          <div className="bp-done">
            <div className="bp-done-ic"><i className="ti ti-circle-check" aria-hidden="true" /></div>
            <div className="bp-done-h">Application received</div>
            <div className="bp-done-sub">
              Thanks, <strong>{form.name.trim() || "partner"}</strong>. Your <strong>{t.label}</strong> application for the Global
              Reseller Program has been submitted. Our partnerships team will reach out within 2 business
              days to set up your 25% revenue-share account and referral dashboard.
            </div>
            <div className="bp-foot" style={{ justifyContent: "center", marginTop: 18 }}>
              <button className="submit" onClick={onBack} data-testid="bp-done-back">Back to program</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BecomePartnerFlow;
